import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * SortableFieldItem — drag-handle-only sortable wrapper for FormBuilder fields.
 *
 *  - Uses @dnd-kit's useSortable so the parent <SortableContext> drives ordering.
 *  - Listeners + attributes are attached ONLY to the grip handle (drag-from-handle UX).
 *  - The card itself is NOT draggable, so users can interact with inputs/toggles
 *    inside without triggering accidental drags.
 *  - A 2px insertion bar above the active drop target makes the drop position obvious.
 *
 * Props mirror the existing field-card shell so the body markup is unchanged.
 */
export default function SortableFieldItem({
  id,
  index,
  isCritical,
  isHighlighted,
  className = "",
  testId,
  children,
  isOver,
  insertPosition, // 'before' | 'after' | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 200ms cubic-bezier(0.2, 0, 0, 1)",
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Insertion indicator — above */}
      {isOver && insertPosition === "before" && (
        <div
          className="absolute -top-1.5 left-0 right-0 h-1 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.2)] pointer-events-none z-10"
          data-testid="drop-indicator-before"
        />
      )}
      <div
        className={`p-4 bg-muted/30 rounded-xl border-2 transition-colors ${
          isCritical
            ? "border-purple-200/60 bg-purple-50/30"
            : "border-transparent hover:border-primary/20"
        } ${isHighlighted ? "ring-2 ring-primary ring-offset-2" : ""} ${
          isDragging ? "opacity-40" : ""
        } ${className}`}
        data-testid={testId}
        data-field-id={id}
      >
        <div className="flex items-start gap-4">
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing pt-2 touch-none select-none text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Drag to reorder field ${index + 1}`}
            data-testid={`field-drag-handle-${index}`}
          >
            <GripVertical className="w-5 h-5" />
          </button>

          <div className="flex-1 grid gap-4">{children}</div>
        </div>
      </div>
      {/* Insertion indicator — below */}
      {isOver && insertPosition === "after" && (
        <div
          className="absolute -bottom-1.5 left-0 right-0 h-1 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.2)] pointer-events-none z-10"
          data-testid="drop-indicator-after"
        />
      )}
    </div>
  );
}
